/// <amd-module name="Repos/Views/PullRequest/Discussion/PullRequestDiscussionThreadService" />
import { translateStorageKeysToDisplayNames } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import { Comment, CommentThreadStatus, GitPullRequestCommentThread, GitRepository } from "Repos/Common/Contracts/Git";
import { IDiscussionThreadModel, IDiscussionThreadStore } from "Repos/Discussion/Common/DiscussionContracts";
import { DiscussionThreadModel } from "Repos/Discussion/DiscussionThread";
import { DiscussionThreadPermissions } from "Repos/Discussion/DiscussionThread.Types";
import { isActiveCommentStatus } from "Repos/Discussion/Utils/DiscussionStatus";
import { IProjectData } from "Tfs/Platform/Page";
import { IObservableArrayEventArgs, IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssPageService } from "VSS/Platform/Page";
import { IInternalPullRequestDataService, ThreadContextsForLatestChangesMap } from "../PullRequestDataService";
import { hasPermission, PullRequestPermissions } from "../PullRequestPermissionService";
import { getVisitData } from "../Util/Visit";
import { getDiscussionFilterService, IPullRequestDiscussionFilterService } from "./PullRequestDiscussionFilterService";
import { getPullRequestDiscussionUIService, IPullRequestDiscussionContext } from "./PullRequestDiscussionUIService";

export interface IGitPullRequestCommentThreadModel extends IDiscussionThreadModel<GitPullRequestCommentThread> {}

export interface IPullRequestDiscussionThreadService extends IVssService, IDiscussionThreadStore {
    getDiscussionThreads: () => IReadonlyObservableArray<IGitPullRequestCommentThreadModel>;
    getThreadContextsForLatestChanges: () => ThreadContextsForLatestChangesMap;
    addThreadRemovedListener(listener: () => void): void;
    addThread(thread: IGitPullRequestCommentThreadModel): void;
    applyFilter(thread: IGitPullRequestCommentThreadModel[], visible?: boolean, expanded?: boolean): void;
    getFilteredThreads(): IReadonlyObservableArray<IGitPullRequestCommentThreadModel>;
    getResolvedThreadCount(): IReadonlyObservableValue<number>;
    getThreadContext(): IPullRequestThreadContext;
    getTotalThreadCount(): IReadonlyObservableValue<number>;
    hasUnsavedThreads(): boolean;
    removeThread(threadId: number): void;
    removeThreadRemovedListener(listener: () => void): void;
    saveThreads(): Promise<void>;
    updateThread(threadId: number): void;
    updateThreadStatus(): void;
}

export interface IPullRequestThreadContext {
    currentUser: IdentityRef;
    permissions: DiscussionThreadPermissions;
    personaProvider: PersonaDetailsProvider;
    project: IProjectData;
    pullRequestId: number;
    repository: GitRepository;
}

class PullRequestDiscussionThreadService extends VssService implements IPullRequestDiscussionThreadService {
    private discussionThreads = new ObservableArray<IGitPullRequestCommentThreadModel>();
    private personaProvider: PersonaDetailsProvider;
    private threadRemovedListeners: (() => void)[] = [];
    private threadContext: IPullRequestThreadContext;
    private user: IdentityRef;
    private creationThread: IGitPullRequestCommentThreadModel;
    private lastVisitedDate: Date;
    private resolvedThreadCount = new ObservableValue(0);
    private totalThreadCount = new ObservableValue(0);

    private dataService: IInternalPullRequestDataService;
    private filterService: IPullRequestDiscussionFilterService;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.personaProvider = new PersonaDetailsProvider(this.pageContext);
        this.user = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user;

        const lastVisit = getVisitData(pageContext);
        this.lastVisitedDate = (lastVisit && lastVisit.previousLastVisitedDate) || new Date();

        this.dataService = pageContext.getService<IInternalPullRequestDataService>("IInternalPullRequestDataService");
        const discussionThreads = this.dataService.getDiscussionThreads();
        discussionThreads.subscribe(this.onThreadsUpdate);

        const data = this.dataService.getPullRequestBasicData().value;
        const pullRequest = data.pullRequest;

        let permissions: DiscussionThreadPermissions = DiscussionThreadPermissions.None;
        if (hasPermission(pageContext, PullRequestPermissions.AddEditComment)) {
            permissions = permissions | DiscussionThreadPermissions.ManageComment;
        }

        if (hasPermission(pageContext, PullRequestPermissions.LikeComment)) {
            permissions = permissions | DiscussionThreadPermissions.LikeComment;
        }

        if (hasPermission(pageContext, PullRequestPermissions.UpdateCommentStatus)) {
            permissions = permissions | DiscussionThreadPermissions.ChangeStatus;
        }

        const repository = pullRequest.repository;

        this.threadContext = {
            currentUser: this.user,
            permissions,
            personaProvider: this.personaProvider,
            project: repository.project,
            pullRequestId: pullRequest.pullRequestId,
            repository
        };

        // Add creation thread manually
        this.creationThread = new DiscussionThreadModel<GitPullRequestCommentThread>({
            thread: {
                id: 0,
                comments: [] as Comment[],
                identities: { "1": pullRequest.createdBy } as { [key: string]: IdentityRef },
                isDeleted: false,
                lastUpdatedDate: pullRequest.creationDate,
                properties: {
                    CodeReviewStatus: { $type: "System.String", $value: "Create" },
                    CodeReviewStatusUpdatedByIdentity: { $type: "System.String", $value: "1" },
                    CodeReviewThreadType: { $type: "System.String", $value: "StatusUpdate" }
                },
                publishedDate: pullRequest.creationDate,
                status: CommentThreadStatus.Active
            } as GitPullRequestCommentThread
        });

        this.filterService = getDiscussionFilterService(pageContext);
        this.processThreads(discussionThreads.value);
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);

        const discussionThreads = this.dataService.getDiscussionThreads();
        discussionThreads.unsubscribe(this.onThreadsUpdate);

        // @ts-ignore TypeScript 4.0 upgrade
        delete this.dataService;
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.filterService;
    }

    public addThreadRemovedListener(listener: () => void): void {
        this.threadRemovedListeners.push(listener);
    }

    public addThread(thread: IGitPullRequestCommentThreadModel): void {
        this.discussionThreads.push(thread);
    }

    public applyFilter(threads: IGitPullRequestCommentThreadModel[], visible?: boolean, expanded?: boolean): void {
        this.filterService.applyFilterOnThreads(threads, visible, expanded);
    }

    public getThreadContextsForLatestChanges(): ThreadContextsForLatestChangesMap {
        return this.dataService.getThreadContextsForLatestChanges();
    }

    public getDiscussionThreads(): IReadonlyObservableArray<IGitPullRequestCommentThreadModel> {
        return this.discussionThreads;
    }

    public getFilteredThreads(): IReadonlyObservableArray<IGitPullRequestCommentThreadModel> {
        return this.filterService.getFilteredThreads();
    }

    public getResolvedThreadCount(): IReadonlyObservableValue<number> {
        return this.resolvedThreadCount;
    }

    public getThreadContext(): IPullRequestThreadContext {
        return this.threadContext;
    }

    public getTotalThreadCount(): IReadonlyObservableValue<number> {
        return this.totalThreadCount;
    }

    public hasUnsavedThreads(): boolean {
        return this.discussionThreads.value.some(t => t.isDirty().value);
    }

    public removeThread(threadId: number): void {
        const deleteIndex = this.discussionThreads.value.findIndex(t => t.id === threadId);
        if (deleteIndex >= 0) {
            this.discussionThreads.splice(deleteIndex, 1);
            this.updateThreadStatus();

            for (const listener of this.threadRemovedListeners) {
                listener();
            }
        }
    }

    public removeThreadRemovedListener(listener: () => void): void {
        const index = this.threadRemovedListeners.indexOf(listener);
        if (index >= 0) {
            this.threadRemovedListeners.splice(index, 1);
        }
    }

    public async saveThreads(): Promise<void> {
        const { project, pullRequestId, repository } = this.threadContext;
        const discussionUIService = getPullRequestDiscussionUIService(this.pageContext);

        const dirtyThreads = this.discussionThreads.value.filter(t => t.isDirty().value);
        for (const thread of dirtyThreads) {
            const discussionContext: IPullRequestDiscussionContext = {
                project: project.id,
                pullRequestId: pullRequestId,
                repositoryId: repository.id,
                thread
            };

            await discussionUIService.saveThread(discussionContext);
        }
    }

    public updateThread(threadId: number): void {
        const threadIndex = this.discussionThreads.value.findIndex(t => t.id === threadId);
        if (threadIndex >= 0) {
            this.discussionThreads.splice(threadIndex, 1, ...[this.discussionThreads.value[threadIndex]]);
            this.updateThreadStatus();
        }
    }

    public updateThreadStatus(): void {
        const discussionThreads = this.discussionThreads.value.filter(thread => thread.id > 0 && thread.getStatus().value !== undefined);

        if (this.totalThreadCount.value !== discussionThreads.length) {
            this.totalThreadCount.value = discussionThreads.length;
        }

        let resolvedCount = 0;
        for (const thread of discussionThreads) {
            if (!isActiveCommentStatus(thread.getStatus().value)) {
                resolvedCount += 1;
            }
        }

        if (this.resolvedThreadCount.value !== resolvedCount) {
            this.resolvedThreadCount.value = resolvedCount;
        }
    }

    private processThreads(threads: GitPullRequestCommentThread[]): void {
        const discussionThreads: IGitPullRequestCommentThreadModel[] = [];

        // Add creation thread
        discussionThreads.push(this.creationThread);

        // Add temp threads
        for (const thread of this.discussionThreads.value.filter(t => t.isNew())) {
            discussionThreads.push(thread);
        }

        const threadMap: Map<number, IGitPullRequestCommentThreadModel> = new Map();
        this.discussionThreads.value.forEach(t => threadMap.set(t.id, t));
        const threadPromises: Promise<void>[] = [];

        // Add the threads coming from the server
        for (const thread of threads) {
            let threadModel = threadMap.get(thread.id);
            const addThreads = () => {
                if (!thread.isDeleted) {
                    if (threadModel) {
                        threadModel.update(thread);
                        discussionThreads.push(threadModel);
                    } else {
                        threadModel = new DiscussionThreadModel({ thread });
                        threadModel.setExpanded(false);
                        discussionThreads.push(threadModel);
                    }

                    // Make sure we update unread info since these are the threads coming from the server
                    threadModel.setUnread(this.user, this.lastVisitedDate);
                } else {
                    // If there are else where deleted threads but still being edited here, keep them
                    if (threadModel && threadModel.isDirty().value) {
                        threadModel.update(thread);
                        discussionThreads.push(threadModel);
                    }
                }
            };

            if (threadModel && useStorageKeysInMentions(this.pageContext)) {
                const promises = thread.comments.map(comment =>
                    translateStorageKeysToDisplayNames(this.pageContext, comment.content).then(
                        translatedContent => (comment.content = translatedContent)
                    )
                );

                // Once all the translations are complete, update the thread
                threadPromises.push(Promise.all(promises).then(() => addThreads()));
            } else {
                addThreads();
            }
        }

        Promise.all(threadPromises).then(() => {
            discussionThreads.sort((t1, t2) => t1.thread.publishedDate.getTime() - t2.thread.publishedDate.getTime());
            this.discussionThreads.value = discussionThreads;
            this.filterService.updateDiscussionThreads(discussionThreads);
            this.updateThreadStatus();
        });
    }

    private onThreadsUpdate = (args: IObservableArrayEventArgs<GitPullRequestCommentThread>): void => {
        this.processThreads(args.addedItems || []);
    };
}

const serviceName = "IPullRequestDiscussionThreadService";
Services.add(serviceName, { serviceFactory: PullRequestDiscussionThreadService });

export function getPullRequestDiscussionService(pageContext: IVssPageContext): IPullRequestDiscussionThreadService {
    return pageContext.getService<IPullRequestDiscussionThreadService>(serviceName);
}
